'use client';

import type * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Shared with `TableRowHeader` so a row header is a cell that differs only in
 * element and scope -- never in its box.
 */
const TABLE_CELL_CLASS =
  'p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]';

interface TableProps extends React.ComponentProps<'table'> {
  /**
   * The table's accessible name, rendered as a visually hidden `<caption>`.
   *
   * A prop rather than a `<TableCaption>` the caller places, because a
   * `<caption>` is only valid as the first child of `<table>` -- a rule that is
   * easy to break by hand and invisible when you do. Required because a table
   * announces itself by nothing else: unnamed, it reads to a screen reader as
   * an anonymous grid, and a page with two of them offers no way to tell which
   * is which.
   */
  caption: string;
}

function Table({ caption, children, className, ...props }: TableProps) {
  return (
    <div data-slot="table-container" className="relative w-full overflow-x-auto">
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      >
        <TableCaption className="sr-only">{caption}</TableCaption>
        {children}
      </table>
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead data-slot="table-header" className={cn('[&_tr]:border-b', className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('bg-muted/50 border-t font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors',
        className
      )}
      {...props}
    />
  );
}

/**
 * A column header. Use `TableRowHeader` for a cell that names its row -- this
 * one carries the header sizing and would resize the body row.
 */
function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      scope="col"
      className={cn(
        'text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return <td data-slot="table-cell" className={cn(TABLE_CELL_CLASS, className)} {...props} />;
}

/**
 * The cell that names its row, so a screen reader repeats "Acme Corp" as the
 * user moves across the row instead of reading bare values.
 *
 * `text-left font-normal` undo the `<th>` user-agent defaults (bold, centred)
 * that `TABLE_CELL_CLASS` doesn't set, so promoting a `<td>` here is a semantic
 * change with no visual one.
 */
function TableRowHeader({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-row-header"
      scope="row"
      className={cn(TABLE_CELL_CLASS, 'text-left font-normal', className)}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('text-muted-foreground mt-4 text-sm', className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableRowHeader,
  TableCell,
  TableCaption,
};
